import { useCallback, useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../lib/motion';
import { SECTIONS, type SectionId } from './sections';

/** A section counts as the one in view once its top is this far (or less) below the top bar. */
const LINE_BELOW_BAR = 120;
/** After a click the underline stays on the target while the smooth scroll passes the sections between. */
const SCROLL_LOCK_MS = 800;
const FALLBACK_BAR_HEIGHT = 60;

/**
 * Index of the section in view, given each section's distance from the top of the viewport
 * (`null` when it is not on the page). The last section whose top has crossed `line` wins,
 * and at the very bottom of the page the last section wins. -1 when no section is on the page.
 */
export function pickActiveSection(tops: Array<number | null>, line: number, atBottom: boolean): number {
  const present = tops.flatMap((top, index) => (top === null ? [] : [index]));
  if (present.length === 0) return -1;
  if (atBottom) return present[present.length - 1];
  let picked = present[0];
  for (const index of present) {
    if ((tops[index] as number) <= line) picked = index;
  }
  return picked;
}

function barHeight(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--topbar-h');
  const value = parseFloat(raw);
  return Number.isFinite(value) ? value : FALLBACK_BAR_HEIGHT;
}

/**
 * Follows which console section is in view (item 43). `goTo` scrolls to a section and moves the
 * marker at once. Pass `enabled = false` off the console, where the sections do not exist.
 */
export function useActiveSection(enabled: boolean) {
  const [active, setActive] = useState<SectionId>(SECTIONS[0].id);
  const lockUntil = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (Date.now() < lockUntil.current) return;
      const tops = SECTIONS.map(
        (section) => document.getElementById(section.anchor)?.getBoundingClientRect().top ?? null
      );
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const index = pickActiveSection(tops, barHeight() + LINE_BELOW_BAR, atBottom);
      if (index >= 0) setActive(SECTIONS[index].id);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [enabled]);

  const goTo = useCallback((id: SectionId, behavior: ScrollBehavior = 'smooth') => {
    const section = SECTIONS.find((item) => item.id === id);
    const element = section ? document.getElementById(section.anchor) : null;
    if (!element) return;
    const smooth = behavior === 'smooth' && !prefersReducedMotion();
    lockUntil.current = smooth ? Date.now() + SCROLL_LOCK_MS : 0;
    setActive(id);
    element.scrollIntoView?.({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  }, []);

  return { active, goTo };
}
